/**
 * GDataPartner — Dedizierte G DATA Partner-Landing-Page (TQ #6906).
 *
 * Patric 2026-06-02: Eigene Seite fuer das volle G DATA-Lineup statt
 * vereinzelter Karten im Marktplatz. Zeigt B2C (AV / IS / TS / VPN /
 * Identity Protection), B2B (Endpoint Protection, Managed XDR, ADAN
 * Incident Response, CDAT Awareness) und verlinkt zu Angebots-Mailto
 * an vertrieb@it-sce.de.
 *
 * Assets unter org-portal/public/brand/ (Box-Shots aus IT-SCE Lieferanten-
 * Verzeichnis). Texte sind heute hartgecoded — sobald die G DATA-Items in
 * ERPNext gepflegt sind, kann die Liste aus subscription_plans + portal_category
 * geladen werden.
 */

import { Link } from 'react-router-dom'
import './Services.css'

interface ProductRow {
  slug: string
  name: string
  subtitle?: string
  image: string
  description: string
  priceHint: string
  mailtoSubject: string
}

const B2C: ProductRow[] = [
  {
    slug: 'av',
    name: 'G DATA Antivirus',
    subtitle: 'Basis-Schutz für Windows',
    image: '/brand/gdata-antivirus-box.png',
    description:
      'Echtzeit-Schutz vor Viren, Trojanern und Ransomware. Made in Germany, keine Backdoors, BSI-zertifiziert.',
    priceHint: 'ab ca. 30 €/Jahr (1 Gerät)',
    mailtoSubject: 'G DATA Antivirus Angebot',
  },
  {
    slug: 'is',
    name: 'G DATA Internet Security',
    subtitle: 'Sicher Surfen + Online-Banking',
    image: '/brand/gdata-internet-security-box.png',
    description:
      'AV plus BankGuard, Firewall, Spam-Schutz und Kindersicherung. Empfohlen für Familien-PCs und Online-Banking.',
    priceHint: 'ab 33,57 €/Jahr (3 Geräte)',
    mailtoSubject: 'G DATA Internet Security Angebot',
  },
  {
    slug: 'ts',
    name: 'G DATA Total Security',
    subtitle: 'Komplettpaket mit Backup',
    image: '/brand/gdata-total-security-box.png',
    description:
      'Alles aus IS plus Passwort-Manager, Datentresor und automatische Backups. Multi-Plattform: Windows, macOS, Android, iOS.',
    priceHint: 'ab ca. 50 €/Jahr',
    mailtoSubject: 'G DATA Total Security Angebot',
  },
  {
    slug: 'vpn',
    name: 'G DATA VPN',
    subtitle: 'Verschlüsselt im Netz',
    image: '/brand/gdata-vpn-box.png',
    description:
      'Schützt deine Identität im öffentlichen WLAN. Server in über 75 Standorten, Strict-No-Log-Policy.',
    priceHint: 'ab ca. 40 €/Jahr',
    mailtoSubject: 'G DATA VPN Angebot',
  },
  {
    slug: 'idp',
    name: 'G DATA Identity Protection',
    subtitle: 'Identitätsdiebstahl-Schutz',
    image: '/brand/gdata-identity-box.png',
    description:
      'Warnt dich, wenn deine Mail-Adresse oder Kontodaten im Darknet auftauchen. Inkl. Soforthilfe im Schadensfall.',
    priceHint: 'auf Anfrage',
    mailtoSubject: 'G DATA Identity Protection Angebot',
  },
]

interface B2BBlock {
  emoji: string
  title: string
  description: string
  mailtoSubject: string
}

const B2B: B2BBlock[] = [
  {
    emoji: '🏢',
    title: 'Endpoint Protection',
    description:
      'Zentral verwalteter Virenschutz für alle Mitarbeiter-PCs, Server und mobile Geräte. Inkl. PolicyManager, Patch-Management und MobileDevice-Management.',
    mailtoSubject: 'G DATA Endpoint Protection Angebot',
  },
  {
    emoji: '🔭',
    title: 'Managed Extended Detection & Response (MXDR)',
    description:
      'Rund-um-die-Uhr-Überwachung deiner Endpoints durch das G DATA SOC. Bei Vorfällen direkte Reaktion durch echte Analysten — nicht nur Alerts.',
    mailtoSubject: 'G DATA Managed XDR Angebot',
  },
  {
    emoji: '🚑',
    title: 'ADAN Incident Response Retainer',
    description:
      'Ernstfall-Hotline mit garantierter Reaktionszeit. Forensik, Schadensbegrenzung und Wiederanlauf nach Cyber-Angriffen — bevor du eine eigene Notfall-Liste brauchst.',
    mailtoSubject: 'G DATA ADAN Incident Response Angebot',
  },
  {
    emoji: '🎓',
    title: 'Cyber Defense Awareness Training (CDAT)',
    description:
      'Online-Lernpfad für deine Mitarbeiter — Phishing, sichere Passwörter, sensible Daten, BSI-Grundlagen. Mit Reporting-Dashboard für die Geschäftsleitung.',
    mailtoSubject: 'G DATA CDAT Awareness-Training Angebot',
  },
  {
    emoji: '🛠',
    title: 'Penetration-Tests & Security Assessments',
    description:
      'Externe Sicherheitsprüfung deiner IT-Infrastruktur durch das G DATA Advanced Analytics Team. Findet Schwachstellen, bevor sie ausgenutzt werden.',
    mailtoSubject: 'G DATA Pentest / Assessment Angebot',
  },
]

function mailto(subject: string): string {
  const body = encodeURIComponent(
    `Hallo IT-SCE-Team,\n\nich interessiere mich für: ${subject}.\n\nBitte sendet mir ein passendes Angebot zu.\n\nDanke!\n`,
  )
  return `mailto:vertrieb@it-sce.de?subject=${encodeURIComponent(subject)}&body=${body}`
}

export default function GDataPartner() {
  return (
    <div className="page">
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
        border: '1px solid #e2e8f0',
        borderRadius: 16,
        padding: '2rem',
        marginBottom: '2rem',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: '1.5rem',
        alignItems: 'center',
      }}>
        <img
          src="brand/gdata-cyberdefense-logo.png"
          alt="G DATA CyberDefense"
          style={{ width: 140, height: 'auto' }}
        />
        <div>
          <h1 style={{ margin: '0 0 0.5rem', color: '#0f172a' }}>
            G DATA Sicherheitslösungen
          </h1>
          <p style={{ margin: '0 0 0.5rem', color: '#475569', fontSize: '1rem' }}>
            IT-SCE ist offizieller G DATA Partner und Reseller. Made in Germany —
            zertifiziert, kein Daten-Abfluss, BSI-anerkannt.
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#0f172a' }}>
            Alle Produkte mit individueller Beratung und passender Lizenz für deine
            Geräte-Anzahl und Laufzeit.
          </p>
        </div>
      </div>

      {/* B2C Lineup */}
      <h2 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>Für Privatpersonen</h2>
      <div className="services-grid" style={{ marginBottom: '2.5rem' }}>
        {B2C.map(p => (
          <div key={p.slug} className="service-card">
            <div className="service-header">
              <img
                src={p.image}
                alt={p.name}
                style={{ width: 72, height: 92, objectFit: 'contain', flexShrink: 0 }}
              />
              <div className="service-info">
                <div className="service-name">{p.name}</div>
                {p.subtitle && (
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{p.subtitle}</div>
                )}
              </div>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0.5rem 0 1rem', flex: 1 }}>
              {p.description}
            </p>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginTop: 'auto', paddingTop: '0.5rem',
            }}>
              <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{p.priceHint}</div>
              <a href={mailto(p.mailtoSubject)} className="btn-primary" style={{
                padding: '0.5rem 1rem', fontSize: '0.9rem', textDecoration: 'none',
              }}>
                Angebot anfragen
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* B2B Lineup */}
      <h2 style={{ marginBottom: '1rem', fontSize: '1.2rem' }}>Für Unternehmen</h2>
      <div className="services-grid">
        {B2B.map(b => (
          <div key={b.title} className="service-card">
            <div className="service-header">
              <div className="service-icon" aria-hidden style={{ fontSize: '1.6rem' }}>{b.emoji}</div>
              <div className="service-info">
                <div className="service-name">{b.title}</div>
              </div>
            </div>
            <p style={{ fontSize: '0.9rem', color: '#4b5563', margin: '0.5rem 0 1rem', flex: 1 }}>
              {b.description}
            </p>
            <a href={mailto(b.mailtoSubject)} className="btn-primary" style={{
              marginTop: 'auto', textDecoration: 'none',
            }}>
              Angebot anfragen
            </a>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="empty-state" style={{ marginTop: '2.5rem' }}>
        <h2 style={{ margin: '0 0 0.5rem' }}>Persönliche Beratung</h2>
        <p style={{ margin: '0 0 1rem' }}>
          Unsicher welches Paket passt? Wir analysieren mit dir deine
          Anforderungen und schlagen eine konkrete Konfiguration vor — Mail
          an vertrieb@it-sce.de oder direkt über den Support-Tab.
        </p>
        <Link to="/support" className="btn-primary">Support kontaktieren</Link>
      </div>
    </div>
  )
}
